import axios from 'axios';
import utilsStorage from '@/utils/storage';
import { PROFILES } from '../constants/general-constants';
import { describeConnectionError, ensureReferenceOnErrorResponse, sessionExpiredMessage } from '@/api/api';
import { getExternalAccessSessionExpiredMessage } from '@/constants/external-access-constants';
import { isOnCustomerFormExternalAccess } from '@/utils/external-access-route';
import { LOCALIZED_FLAG } from '@/utils/backend-message-i18n';
import { withLocaleHeader } from '@/utils/locale-propagation';

let configCache = null;
let configPromise = null;

async function getConfig() {
  if (configCache) return configCache;

  if (!configPromise) {
    configPromise = fetch('/config.json')
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');

        return response.json();
      })
      .then((data) => {
        configCache = data;

        return data;
      })
      .catch(() => {
        configPromise = null;

        return null;
      });
  }

  return configPromise;
}

function withExternalAccessSessionExpired(error) {
  const data = error.response.data && typeof error.response.data === 'object' ? error.response.data : {};

  // Texto do próprio front, já no idioma da interface
  error.response.data = { ...data, mensagem: getExternalAccessSessionExpiredMessage(), [LOCALIZED_FLAG]: true };

  return error;
}

export default class BaseApi {
  constructor(obj) {
    obj = obj || {};

    let routePrefix = '';

    // Suporte a legacy 'costumerId' e novo 'customerId'
    if (obj.customerId || obj.costumerId) {
      routePrefix = '/customer';
    } else if (obj.correspondentId) {
      routePrefix = '/corban';
    }

    if (String(obj.profileId) === String(PROFILES[0].id)) {
      routePrefix = '/admin';
    }

    this._routePrefix = typeof obj.prefix === 'string' ? obj.prefix : routePrefix;

    // api criada sem baseURL; o interceptor de request resolve antes de cada chamada
    this.api = axios.create({});
    this.urlApi = null;
    this.urlBase = null;

    this._createInterceptors();
  }

  _createInterceptors() {
    this.api.interceptors.request.use(async (config) => {
      const appConfig = await getConfig();

      if (!appConfig?.urlApi) {
        return Promise.reject(new Error('Configuração da aplicação não disponível.'));
      }

      if (!config.baseURL) {
        config.baseURL = appConfig.urlApi + this._routePrefix;
        this.urlApi = appConfig.urlApi;
        this.urlBase = appConfig.urlBase;
      }

      const token = utilsStorage.getTokenOnStorage();
      config.headers.Authorization = token;

      return withLocaleHeader(config);
    });

    const isPublicRoute = () => {
      const currentPath = window.location.pathname;
      // Precisa espelhar as rotas com meta.requireAuth === false no router:
      // sem isso um 401 de fundo derruba a página pública para o /login.
      const publicPaths = ['/operacao/', '/login', '/assinatura', '/confirmacao-assinatura', '/external-access', '/external-acess/', '/ui', '/cadastro/'];

      return publicPaths.some((path) => currentPath.includes(path));
    };

    this.api.interceptors.response.use(
      (response) => {
        if (response.status === 401 && response.config.url !== '/login' && !isPublicRoute()) {
          utilsStorage.removeAuth();

          setTimeout(() => {
            // Reverifica AQUI, não só quando o 401 chegou: entre a resposta e
            // este disparo o usuário pode ter ido para uma rota pública, e o
            // redirect pendente o arrancava dela.
            if (isPublicRoute()) return;

            // Guarda onde o usuario estava para o login devolve-lo la (ex.: /suporte).
            utilsStorage.saveCurrentLocationAsReturnPath();
            window.location.href = '/login';
          }, 1000);
        }

        return response;
      },
      (error) => {
        if (!error.response) return Promise.reject(describeConnectionError());

        const isUnauthorized = error.response.status === 401;
        const isLoginRoute = error.response.config.url === '/login';
        const hasValidBaseUrl = !!error.response.config.baseURL;

        if (isUnauthorized && isOnCustomerFormExternalAccess()) return Promise.reject(withExternalAccessSessionExpired(error));

        if (isUnauthorized && !isLoginRoute && !isPublicRoute() && hasValidBaseUrl) {
          utilsStorage.removeAuth();

          setTimeout(() => {
            // Reverifica AQUI, não só quando o 401 chegou: entre a resposta e
            // este disparo o usuário pode ter ido para uma rota pública, e o
            // redirect pendente o arrancava dela.
            if (isPublicRoute()) return;

            // Guarda onde o usuario estava para o login devolve-lo la (ex.: /suporte).
            utilsStorage.saveCurrentLocationAsReturnPath();
            window.location.href = '/login';
          }, 3000);

          return Promise.reject(sessionExpiredMessage());
        }

        return Promise.reject(ensureReferenceOnErrorResponse(error));
      }
    );
  }
}

export { getConfig };
